import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
} from '@angular/core';

import { StockItem } from 'src/app/core';
import { AnimationItem } from 'lottie-web';
import { FormBuilder } from '@angular/forms';
import { printOrderAnimations } from '../../animation';
import { digitsEnToFa } from '@persian-tools/persian-tools';
import { PrintBlockItem, rangeValidator, SelectorType } from 'src/app/shared';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'print-mixed-range-select',
  templateUrl: './print-mixed-range-select.component.html',
  styleUrls: ['./print-mixed-range-select.component.scss'],
  animations: [printOrderAnimations],
})
export class PrintMixedRangeSelectComponent implements OnInit {
  @Input() title!: string;
  @Input() type!: StockItem | undefined;
  @Input() isTwoSided!: boolean;
  @Input() typings!: StockItem[];
  @Input() selector!: SelectorType;
  @Input() blocks!: PrintBlockItem[];
  @Input() disabled = false;
  /**
   * The maximum and minimum of range
   * or page selection
   */
  @Input() max?: number;
  @Input() min?: number = 1;

  @Output() onChange = new EventEmitter();
  @Output() blocksChange = new EventEmitter<PrintBlockItem[]>();

  private animationItems: { [key: string]: AnimationItem } = {};
  public addButtonOptions = {
    path: '../../../../../assets/animation/add-button.json',
    autoplay: false,
  };

  // ------------------------------- Host listeners|
  @HostListener('document:click', ['$event'])
  clickEvent(event: Event) {
    if (
      !this.componentRef.nativeElement.contains(event.target) &&
      this.overlayController
    ) {
      this.toggleInputOverlay();
    }
  }

  // --------------------------- Public Properties|

  public overlayFormGroup = this.formBuilder.group({
    single: [''],
    from: [''],
    to: [''],
  });

  /**
   * Controls the overlay visibility if you
   * set this to true the overlay will be shown
   */
  public overlayController = false;

  /**
   * This variable changes and becomes true when
   * any block is added.
   */

  public touched: boolean = false;

  // --------------------------- Private Properties|

  constructor(
    private formBuilder: FormBuilder,
    private componentRef: ElementRef,
    private snackbar: MatSnackBar
  ) {}

  ngOnInit(): void {}
  ngOnChange(changes: any) {}

  // --------------------------- Public methods|

  public add(value: any) {
    this.touched = true;
    const printBlock = this.generatePrintBlock(value);
    // console.log("-------------Befor-add-------------")
    // console.log("add",this.blocks)
    // console.log("-------------end-add------------")
    if (!this._hasOverlap(printBlock))
      this.blocks.push(printBlock);
    // console.log("-------------after-add-------------")
    // console.log("add",this.blocks)
    // console.log("-------------end-add-------------")
    // this.blocks.push(printBlock);
    this.onChange.emit();
    this.overlayFormGroup.reset();
  }

  public remove(i: number) {
    this.blocks.splice(i, 1);
    this.onChange.emit();

    if (
      this.blocks.length <= 0 ||
      this.blocks.filter((item) => item.stock?.id == this.type?.id).length <= 0
    ) {
      this.touched = false;
    }
    this.overlayFormGroup.reset();
  }

  public convertToPersian(...args: string[]) {
    const string = args.join(' ');
    return digitsEnToFa(string);
  }

  public toggleInputOverlay() {
    this.overlayController = !this.overlayController;
  }

  public addAnimation(name: string, animationItem: AnimationItem) {
    this.animationItems[name] = animationItem;
  }

  public playAnimation(name: string) {
    if (this.animationItems[name]) {
      this.animationItems[name].play();
    }
  }

  public stopAnimation(name: string) {
    if (this.animationItems[name]) {
      this.animationItems[name].stop();
    }
  }

  // --------------------------- Private methods|

  /**
   * When you pass range string like '20-30' or
   * one number like 32, this method will detect
   * the type and creates the suitable printBlockItem
   * for you.
   * @param value {string | number}
   */
  private generatePrintBlock(value: string | number): PrintBlockItem {
    try {
      switch (typeof value) {
        case 'string':
          return this.generatePrintBlockFromString(value);
        case 'number':
          return this._generatePrintBlockFromNumber(value);

        default:
          throw new TypeError(
            'Data type not accepted, acceptable data types are string, number'
          );
      }
    } catch (e) {
      throw e;
    }
  }

  /**
   * Generate Print block from strings like `12-33`.
   * @param value {string} string value to convert
   * @returns
   */
  private generatePrintBlockFromString(value: string): PrintBlockItem {
    const regex = /([^a-zA-Z][0-9]*)([-])([0-9]*)(?!.*[a-zA-Z])/m;

    if (!regex.test(value)) {
      this.snackbar.open('مقادیر خواسته‌شده را به درستی وارد کنید.', 'باشه', {
        duration: 1000,
      });
      throw new TypeError('print block string is invalid');
    } else if (!this?.type || !this.type?.id) {
      throw new TypeError('Any Type recognized !');
    }

    const ranges = value.split('-');
    const start = +ranges[0];
    const end = +ranges[1];

    if (!this._checkIsInValidRange(start, end)) {
      throw new Error('The range is not valid.');
    }

    return {
      end,
      id: 0,
      start,
      // isLocal: true,
      stock: this.type,
      // actualData: value,
      typeId: this.type.id,
    };
  }

  private _generatePrintBlockFromNumber(value: number): PrintBlockItem {
    if (value < 1) {
      throw new TypeError('Number range should be bigger than 0');
    } else if (!this?.type || !this.type?.id) {
      throw new TypeError('Any Type recognized !');
    }

    const start = value;
    const end = value;

    if (!this._checkIsInValidRange(start, end)) {
      throw new Error('Income range is invalid');
    }

    return {
      end,
      id: 0,
      start,
      // isLocal: true,
      stock: this.type,
      // actualData: value,
      typeId: this.type.id,
    };
  }

  public _checkIsInValidRange(start: number, end: number) {
    if (!this.checkMinimumAndMaximum(start, end)) {
      return false;
    }

    return true;
  }

  public checkMinimumAndMaximum(start: number, end: number): boolean {
    const min = this.getMinimum();
    const max = this.getMaximum();

    if (start < min || start == 0) {
      this.snackbar.open(
        `شماره صفحه ${
          start != end ? 'ابتدای بازه' : ''
        } باید بیشتر از ${min} باشد`,
        'باشه',
        {
          duration: 3000,
        }
      );

      return false;
    }

    if (max && start > max) {
      this.snackbar.open(
        `شماره صفحه ${
          start != end ? 'ابتدای بازه' : ''
        } باید کمتر از ${max} باشد`,
        'باشه',
        {
          duration: 3000,
        }
      );

      return false;
    }

    if (max && end > max) {
      this.snackbar.open(
        `شماره صفحه ${
          start != end ? 'انتهای بازه' : ''
        } باید کمتر از ${max} باشد`,
        'باشه',
        {
          duration: 3000,
        }
      );

      return false;
    }

    return true;
  }

  public getMinimum() {
    return this.min || 1;
  }

  public getMaximum(): number | null {
    return this.max || null;
  }

  public _hasOverlap(newPrintblock: any): boolean {
    for (const interval of this.blocks) {
      // Check if there is an overlap
      if (
        (newPrintblock.start >= interval.start && newPrintblock.start <= interval.end) ||
        (newPrintblock.end >= interval.start && newPrintblock.end <= interval.end) ||
        (interval.start >= newPrintblock.start && interval.start <= newPrintblock.end)
      ) {
        this.snackbar.open(
          `صفحه ورودی تکراری می باشد`,
          'باشه',
          { duration: 3000 }
        );
        return true; // Overlap detected
      }
    }

    return false; // No overlap detected
  }

}
